/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Alert, AutoComplete, Button, Col, Form, Input, Row, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { LANGUAGE_OPTIONS } from '../../../../constants/main';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';

// Constant declarations
const { Text } = Typography;

/*
* Handle built-in Webchat actions. The naming convention of the
* built-in actions in the Console should match that of the methods
* in hbf-webchat, e.g webchat_enableSendBox => this.enableSendBox
*/
const HBF_WEBCHAT_CHANNEL_ACTIONS = {
  ENABLE_SENDBOX: 'webchat_enableSendBox',
  DISABLE_SENDBOX: 'webchat_disableSendBox',
  PAUSE_IDLE_NOTIFICATIONS: 'webchat_pauseIdleNotifications'
};

const ChannelActionNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages } = useBotLanguage();

  // The array of languages that have changed labels
  const [langsWithChangedLabels, setLangsWithChangedLabels] = React.useState([]);

  // If any of the language labels are undefined, set them to an empty string
  // It is needed as backend compilation is not triggered if a secondary lang label is missing
  // TODO: Remove this, along with the Alert and Inputs (if needed) once it is handled by the backend
  useEffect(() => {
    setLangsWithChangedLabels(botLanguages.filter(lang => form.getFieldValue(['data', 'label', lang]) === undefined));
    botLanguages.forEach(lang => {
      if (!form.getFieldValue(['data', 'label', lang])) {
        form.setFieldValue(['data', 'label', lang], '');

        // Trigger validation to notify the form that it has changed
        // No need to trigger validation if the action name is empty, as the user will edit it anyway and trigger validation
        if (!(isEditingPrimaryLanguage && !form.getFieldValue(['data', 'actionData', 'name']))) {
          form.validateFields();
        }
      }
    });
  }, [isEditingPrimaryLanguage]);

  return (
    <Fragment>
      {
        langsWithChangedLabels.length > 0 && !(isEditingPrimaryLanguage && !form.getFieldValue(['data', 'actionData', 'name'])) ? (
          <Alert
            className="mb-3"
            // lang should have all the languages that have changed labels comma separated but each should look like
            // LANGUAGE_OPTIONS.find(lang => lang.key === langsWithChangedLabels[0])?.label
            message={t(`Click Done to submit action configuration for {{lang}} language.`, {
              lang: langsWithChangedLabels.map(lang => LANGUAGE_OPTIONS.find(l => l.key === lang)?.label).join(', '),
              id: form.getFieldValue('id')
            })}
            type="warning"
            showIcon
          />
        ) : null
      }

      {
        botLanguages.map(lang => (
          <Form.Item
            label=""
            hidden
            name={['data', 'label', lang]}
          >
            <Input />
          </Form.Item>
        ))
      }

      <Form.Item
        label={t('Name')}
        name={['data', 'actionData', 'name']}
        rules={[
          { required: true, message: t('Please enter an action name'), whitespace: true }
        ]}
      >
        <AutoComplete
          allowClear
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Spacebar') {
              e.preventDefault();
            }
          }}
          filterOption={true}
          options={[
            {
              label: t('Built-in Webchat actions'),
              options: Object.values(HBF_WEBCHAT_CHANNEL_ACTIONS).map(action => ({ value: action }))
            }
          ]}
          size="large"
          getPopupContainer={triggerNode => triggerNode.parentElement}
          placeholder={t('Enter the name of the action')}
          disabled={!isEditingPrimaryLanguage}
        />
      </Form.Item>

      <Form.Item
        label={
          <Typography.Text>
            {t('Alternative text')}
            <Tooltip
              className="ml-1"
              placement="top"
              title={t('The alternative text is used to describe the action, whenever it is executed.')}
            >
              <QuestionCircleOutlined className="fs-sm" />
            </Tooltip>
          </Typography.Text>
        }
        name={['data', 'altText']}
      >
        <Input
          size="large"
          placeholder={t('Enter the alternative text')}
          disabled={!isEditingPrimaryLanguage}
        />
      </Form.Item>

      <Row gutter={16} wrap={false} className="mb-5">
        <Col flex="auto">
          <Row gutter={16}>
            <Col xs={12}>
              <Text>
                {t('Parameter name')}
              </Text>
            </Col>
            <Col xs={12}>
              <Text>
                {t('Parameter value')}
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col xs={24}>
          <Form.List
            name={['data', 'actionData', 'parameters']}
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                <div className="mb-3">
                  {
                    fields.map(({ key, name, ...restField }, _index) => (
                      <Form.Item
                        key={key}
                        className="mb-4"
                      >
                        <Row wrap={false} gutter={8} align="bottom">
                          <Col flex="auto">
                            <Row gutter={16}>
                              <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'type']}
                                  className="mb-0 hide-error"
                                >
                                  <Input
                                    placeholder={t('Parameter name')}
                                    size="large"
                                    disabled={!isEditingPrimaryLanguage}
                                  />
                                </Form.Item>
                              </Col>

                              <Col sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'value']}
                                  className="mb-0 hide-error"
                                >
                                  <RichTextEditor
                                    toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
                                    placeholder={t('Parameter value')}
                                    hasFloatingToolbar={true}
                                    handleAsPlainText={true}
                                    disabled={!isEditingPrimaryLanguage}
                                    className="ql-sm ql-border-md ql-border-focus ql-font-normal"
                                    inline
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          {
                            fields.length > 1 ? (
                              <Col flex="none">
                                <Button
                                  icon={<DeleteIcon />}
                                  size="large"
                                  type="text"
                                  onClick={() => remove(name)}
                                  disabled={!isEditingPrimaryLanguage}
                                />
                              </Col>
                            ) : null
                          }
                        </Row>
                      </Form.Item>
                    ))
                  }
                </div>

                <Row gutter={16}>
                  <Col sm={12} xs={24}>
                    <Button
                      type="dashed"
                      size="large"
                      block
                      onClick={() => add()}
                      icon={<PlusIcon />}
                      disabled={!isEditingPrimaryLanguage}
                    >
                      {t('Add new parameter')}
                    </Button>
                  </Col>
                </Row>

                <Form.ErrorList errors={errors} />
              </Fragment>
            )}
          </Form.List>
        </Col>
      </Row>
    </Fragment>
  );
};

ChannelActionNodeForm.propTypes = {
  form: PropTypes.object.isRequired
};

export default ChannelActionNodeForm;
